/**
 * Video Player Debug Logging Utility
 *
 * Provides toggleable console logging for video player, preloader, and media loading.
 *
 * HOW TO USE:
 *
 * 1. Enable debug logging:
 *    - Open browser console
 *    - Run: localStorage.setItem('DEBUG_VIDEO_PLAYER', 'true')
 *    - Refresh page
 *
 * 2. Disable debug logging:
 *    - Run: localStorage.removeItem('DEBUG_VIDEO_PLAYER')
 *    - Refresh page
 *
 * 3. In code:
 *    import { debugLog } from '@/lib/videoPlayerDebug';
 *    debugLog.player('Play All clicked');
 *    debugLog.preloader('Preloading video', videoId);
 *    debugLog.media('Video ready', { readyState: 3 });
 *
 * CATEGORIES:
 * - debugLog.player()     - VideoPlayer component events
 * - debugLog.preloader()  - Video preloading operations
 * - debugLog.media()      - MediaRenderer loading events
 * - debugLog.userAction() - Always logs (green badge style, bypasses flag)
 * - debugLog.error()      - Always logs (errors bypass flag)
 * - debugLog.warn()       - Always logs (warnings bypass flag)
 */

// Check if debug mode is enabled (persists across page reloads)
const isDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('DEBUG_VIDEO_PLAYER') === 'true';
};

/**
 * Print the "how to toggle debug logging" hint. Admin-only.
 *
 * Called by `useIsSystemAdmin()` once it has a verified answer — NOT as an
 * import side effect. It used to run on import, which meant a
 * `supabase.auth.getSession()` plus a fetch to `is-system-admin` on **every
 * page load for every user**, purely to decide whether to print a console
 * message. That request was also a straight duplicate: `useIsSystemAdmin()`
 * already asks the same endpoint the same question, and five components use it.
 *
 * Admin status still comes from the server — the hint reveals nothing a
 * non-admin couldn't read in this file anyway.
 */
let hintShown = false;
export function showDebugHintForAdmin(): void {
  if (hintShown || typeof window === 'undefined') return;
  hintShown = true;
  const enabled = isDebugEnabled();
  console.log(
    `%c[Debug] Video player debug logging is ${enabled ? 'ON' : 'OFF'}%c\n` +
    `  Enable:  localStorage.setItem('DEBUG_VIDEO_PLAYER', 'true')\n` +
    `  Disable: localStorage.removeItem('DEBUG_VIDEO_PLAYER')\n` +
    `  Then refresh the page.`,
    enabled ? 'color: #4ade80; font-weight: bold' : 'color: #fbbf24; font-weight: bold',
    'color: inherit'
  );
}

/**
 * Debug logger with category prefixes
 */
export const debugLog = {
  /**
   * Log VideoPlayer component events (Play All, navigation, etc.)
   */
  player: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      console.log('[VideoPlayer]', ...args);
    }
  },

  /**
   * Log preloader operations (fetching, caching, cleanup)
   */
  preloader: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      console.log('[Preloader]', ...args);
    }
  },

  /**
   * Log MediaRenderer loading events (URL fetch, video ready, metadata)
   */
  media: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      console.log('[MediaRenderer]', ...args);
    }
  },

  /**
   * Log user click/interaction events with green badge style.
   * Use for every interactive button/control so console logs tell the complete
   * story of both app behaviour and user actions.
   *
   * Always logs — for ALL users, regardless of the DEBUG_VIDEO_PLAYER flag.
   *
   * @example
   * debugLog.userAction("User clicked 'Play Start'");
   * debugLog.userAction(`Card video - Select - name: ${video.name}`);
   */
  userAction: (message: string) => {
    console.log(`%c${message}`, 'background: #22c55e; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold');
  },

  /**
   * Always log errors (bypasses debug flag)
   */
  error: (...args: unknown[]) => {
    console.error('[VideoPlayer]', ...args);
  },

  /**
   * Always log warnings (bypasses debug flag)
   */
  warn: (...args: unknown[]) => {
    console.warn('[VideoPlayer]', ...args);
  }
};

/**
 * Utility to check if debug is enabled (for conditional expensive operations)
 *
 * @example
 * if (isVideoPlayerDebugEnabled()) {
 *   const stats = calculateExpensiveStats();
 *   debugLog.player('Stats:', stats);
 * }
 */
export const isVideoPlayerDebugEnabled = isDebugEnabled;
